// src/services/brandingApi.ts
//
// Branding público do tenant (multi-tenant, specs/001-multi-tenant US3).
// Consumido ANTES do login: o backend resolve o tenant pelo hostname da página
// (o frontend chama a API por caminho relativo /api) e devolve a identidade visual.

import api from '@/config/axios';

export interface EmailSignatureConfig {
    cor_nome: string;
    cor_departamento: string;
    cores_borda: [string, string, string] | null;
    telefone_padrao: string;
    ddd: string;
    website: string | null;
}

/**
 * Variante da assinatura de e-mail, escolhida pelo domínio do e-mail do dono
 * do perfil (ex.: `@empresa.com`). Régua: `utils/assinaturaPorDominio.ts`.
 */
export interface EmailSignatureVariant {
    id: string;
    nome: string;
    dominios: string[];
    email_signature: EmailSignatureConfig;
    signature_logo?: string;
    signature_bg?: string;
}

export interface TenantBranding {
    /** Nome de exibição do hub (ex.: "Hub SIM Inova") */
    titulo?: string;
    /** Subtítulo sob o nome (default: hostname atual) */
    subtitulo?: string;
    /** URL do logo (opcional) */
    logo?: string;
    /** URL do favicon (opcional) */
    favicon?: string;
    /** URL da capa do perfil (hospedada no R2 do tenant) */
    cover_url?: string;
    /** Cores do tema: { primary, secondary, ... } em hex */
    cores?: Record<string, string>;
    /** Selo acima da tagline na tela de login */
    badge?: string;
    /** Frase principal da tela de login */
    tagline?: string;
    /** Trecho da tagline destacado com gradiente */
    tagline_destaque?: string;
    /** Parágrafo descritivo da tela de login */
    descricao?: string;
    /** Titular do copyright no rodapé (default: nome do tenant) */
    copyright?: string;
    email_from?: string;
    email_from_name?: string;
    email_signature?: EmailSignatureConfig;
    signature_logo?: string;
    signature_bg?: string;
    /** Ausente = só a assinatura padrão. */
    email_signature_variantes?: EmailSignatureVariant[];
    /** ID do projeto Microsoft Clarity deste tenant (telemetria isolada por tenant) */
    clarity_project_id?: string;
    /** Razão social do controlador de dados (LGPD) — páginas públicas */
    controladora_legal?: string;
    /** E-mail do DPO/encarregado (LGPD) — páginas públicas */
    dpo_email?: string;
}

export interface TenantPublicInfo {
    slug: string;
    nome: string;
    branding: TenantBranding;
    enabled_modules: string[];
    /** Tenant tem login corporativo Microsoft configurado */
    sso_enabled: boolean;
    /** Tenant master: só ele exibe a administração de tenants */
    is_master?: boolean;
}

export async function fetchPublicBranding(): Promise<TenantPublicInfo> {
    const { data } = await api.get<TenantPublicInfo>('/public/branding', { timeout: 5000 });
    return data;
}
